import { supabase } from "@/services/supabase/client";
import type { SortOrder } from "@/services/api/incompleteUsers";

export type CompleteUserSortBy = "created_at" | "plan";

export interface CompleteUser {
  id: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  created_at: string;
  has_roles: boolean;
  has_locations: boolean;
  phone_verified: boolean;
  has_active_plan: boolean;
  plan?: string | null;
}

export interface CompleteUsersResponse {
  items: CompleteUser[];
  total: number;
  has_more: boolean;
}

export interface GetCompleteUsersParams {
  limit: number;
  offset: number;
  search?: string | null | undefined;
  sortBy?: CompleteUserSortBy;
  sortOrder?: SortOrder;
}

interface RpcEnvelope {
  ok: boolean;
  error?: string;
  items?: CompleteUser[];
  total?: number;
  has_more?: boolean;
}

const getCompleteUsers = async ({
  limit,
  offset,
  search,
  sortBy,
  sortOrder = "desc",
}: GetCompleteUsersParams): Promise<CompleteUsersResponse> => {
  const { data, error } = await supabase.rpc("get_admin_complete_users", {
    _limit: limit,
    _offset: offset,
    _sort_order: sortOrder,
    ...(sortBy ? { _sort_by: sortBy } : {}),
    ...(search ? { _query_text: search } : {}),
  });

  if (error) {
    console.error("getCompleteUsers error:", error);
    throw new Error(error.message || "Failed to fetch complete users");
  }

  const result = data as RpcEnvelope;
  if (!result?.ok) {
    throw new Error(result?.error || "Failed to fetch complete users");
  }

  return {
    items: result.items ?? [],
    total: result.total ?? 0,
    has_more: result.has_more ?? false,
  };
};

const CompleteUsersService = { getCompleteUsers };

export default CompleteUsersService;
